import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableContainer, TableHead,
  TableBody, TableRow, TableCell, IconButton, Button, Chip, TextField,
  Alert, Tooltip, Skeleton, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Today, Send, Undo,
  CheckCircle, Cancel, Warning, Add, Groups, HourglassTop,
  Delete as DeleteIcon, RestartAlt, Edit as EditIcon,
  StickyNote2 as NoteIcon, StickyNote2Outlined as NoteOutlinedIcon,
} from '@mui/icons-material';
import {
  startOfWeek, endOfWeek, addWeeks, subWeeks, format, eachDayOfInterval,
  parseISO, isToday,
} from 'date-fns';
import { useTimeTracking } from '../context_providers/TimeTrackingContext';
import { useProjects } from '../context_providers/ProjectContext';
import { useNotification } from '../context_providers/NotificationContext';
import { useRBAC } from '../context_providers/RBACContext';
import { TimesheetWeek, TimesheetStatus, TimeEntry, Project, Task, Client } from '../../types';
import apiService from '../../utils/api.service';

// -- Types ------------------------------------------------------------------

interface TimesheetRow {
  projectId: string;
  taskId?: string;
  projectName: string;
  taskName?: string;
  clientName?: string;
  projectColor: string;
  entries: Record<string, TimeEntry[]>;
  dayMinutes: Record<string, number>;
  totalMinutes: number;
}

interface EditCell {
  rowKey: string;
  date: string;
}

// -- Helpers ----------------------------------------------------------------

const formatHoursMinutes = (minutes: number): string => {
  if (minutes === 0) return '0:00';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const parseHoursMinutes = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return 0;

  // Support "H:MM", "H.HH", or plain number (hours)
  if (trimmed.includes(':')) {
    const [hStr, mStr] = trimmed.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr || '0', 10);
    if (isNaN(h) || isNaN(m) || h < 0 || m < 0 || m > 59) return null;
    return h * 60 + m;
  }

  const num = parseFloat(trimmed);
  if (isNaN(num) || num < 0) return null;
  return Math.round(num * 60);
};

const makeRowKey = (projectId: string, taskId?: string): string =>
  taskId ? `${projectId}::${taskId}` : projectId;

const STATUS_CONFIG: Record<TimesheetStatus, { color: 'default' | 'info' | 'success' | 'error'; label: string }> = {
  draft: { color: 'default', label: 'Draft' },
  submitted: { color: 'info', label: 'Submitted' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// -- Component --------------------------------------------------------------

const TimesheetPage: React.FC = () => {
  const { weekEntries, fetchWeekEntries, createEntry, updateEntry, deleteEntry } = useTimeTracking();
  const { projects, getProject } = useProjects();
  const { showSuccess, showError } = useNotification();
  const { hasOrgPermission } = useRBAC();

  const isManager = hasOrgPermission('approve_timesheets');

  const [activeTab, setActiveTab] = useState<'personal' | 'pending' | 'team'>('personal');
  const [pendingTimesheets, setPendingTimesheets] = useState<any[]>([]);
  const [teamTimesheets, setTeamTimesheets] = useState<any[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timesheet, setTimesheet] = useState<TimesheetWeek | null>(null);
  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [editCell, setEditCell] = useState<EditCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Add project+task modal state
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskOptions, setTaskOptions] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // Client name lookup
  const [clientMap, setClientMap] = useState<Map<string, string>>(new Map());

  // Reset week confirmation
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Manager: view/adjust team member's timesheet
  const [memberDetailOpen, setMemberDetailOpen] = useState(false);
  const [memberDetailUser, setMemberDetailUser] = useState<any>(null);
  const [memberEntries, setMemberEntries] = useState<TimeEntry[]>([]);
  const [memberTimesheet, setMemberTimesheet] = useState<any>(null);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);
  const [memberEditCell, setMemberEditCell] = useState<{ entryId: string; field: string } | null>(null);
  const [memberEditValue, setMemberEditValue] = useState('');
  const [memberSaving, setMemberSaving] = useState(false);

  // Cell edit modal state (replaces inline editing)
  const [cellModalOpen, setCellModalOpen] = useState(false);
  const [cellModalRow, setCellModalRow] = useState<TimesheetRow | null>(null);
  const [cellModalDate, setCellModalDate] = useState('');
  const [cellModalTime, setCellModalTime] = useState('');
  const [cellModalNote, setCellModalNote] = useState('');
  const [cellModalSaving, setCellModalSaving] = useState(false);

  const editInputRef = useRef<HTMLInputElement>(null);

  // Fetch clients for name resolution
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await apiService.listClients();
        const data = res.data || res;
        const clients: Client[] = Array.isArray(data) ? data : (data?.clients || []);
        setClientMap(new Map(clients.map(c => [c.id, c.name])));
      } catch {
        // non-critical
      }
    };
    fetchClients();
  }, []);

  // Derive the Monday of the current week
  const weekStart = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate],
  );

  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);

  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd],
  );

  const weekStartISO = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);

  const status: TimesheetStatus = timesheet?.status ?? 'draft';
  const isEditable = status === 'draft' || status === 'rejected';

  // -- Data fetching --------------------------------------------------------

  const loadWeekData = useCallback(async (monday: string) => {
    setIsLoading(true);
    try {
      await fetchWeekEntries(monday);
      try {
        const tsResponse = await apiService.getTimesheet(monday);
        const tsData = tsResponse.data || tsResponse;
        setTimesheet(tsData?.timesheet || tsData);
      } catch {
        // 404 or no timesheet yet -- treat as draft
        setTimesheet(null);
      }
    } catch (err: any) {
      showError(err.message || 'Failed to load timesheet data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchWeekEntries, showError]);

  useEffect(() => {
    loadWeekData(weekStartISO);
  }, [weekStartISO, loadWeekData]);

  // -- Build grid rows from weekEntries (grouped by project+task) -----------

  useEffect(() => {
    const grouped: Record<string, TimeEntry[]> = {};

    for (const entry of weekEntries) {
      const pid = entry.projectId || '__no_project__';
      const tid = entry.taskId || '';
      const key = makeRowKey(pid, tid);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    }

    const builtRows: TimesheetRow[] = Object.entries(grouped).map(([key, entries]) => {
      const firstEntry = entries[0];
      const pid = firstEntry.projectId || '__no_project__';
      const tid = firstEntry.taskId || '';
      const project = pid !== '__no_project__' ? getProject(pid) : undefined;
      const entryMap: Record<string, TimeEntry[]> = {};
      const dayMinutes: Record<string, number> = {};

      for (const day of weekDays) {
        const iso = format(day, 'yyyy-MM-dd');
        const dayEntries = entries.filter(e => e.date === iso);
        entryMap[iso] = dayEntries;
        dayMinutes[iso] = dayEntries.reduce((sum, e) => sum + (Number(e.durationMinutes) || 0), 0);
      }

      const totalMinutes = Object.values(dayMinutes).reduce((s, m) => s + m, 0);

      return {
        projectId: pid,
        taskId: tid || undefined,
        projectName: project?.name || 'No Project',
        taskName: '',  // Will be enriched below
        clientName: project?.clientId ? clientMap.get(project.clientId) : undefined,
        projectColor: project?.color || '#94a3b8',
        entries: entryMap,
        dayMinutes,
        totalMinutes,
      };
    });

    // Preserve manually added empty rows
    setRows(prev => {
      const existingKeys = new Set(builtRows.map(r => makeRowKey(r.projectId, r.taskId)));
      const emptyRows = prev.filter(r => r.totalMinutes === 0 && !existingKeys.has(makeRowKey(r.projectId, r.taskId)));
      // Carry task names forward from prev rows
      const prevMap = new Map(prev.map(r => [makeRowKey(r.projectId, r.taskId), r]));
      for (const row of builtRows) {
        const prevRow = prevMap.get(makeRowKey(row.projectId, row.taskId));
        if (prevRow?.taskName) {
          row.taskName = prevRow.taskName;
        }
      }
      return [...builtRows, ...emptyRows];
    });
  }, [weekEntries, weekDays, getProject, clientMap]);

  // Enrich task names (lazy: fetch when rows change and task names are missing)
  useEffect(() => {
    const enrichTaskNames = async () => {
      const rowsNeedingNames = rows.filter(r => r.taskId && !r.taskName);
      if (rowsNeedingNames.length === 0) return;

      const fetched = new Map<string, string>();
      const projectIds = Array.from(new Set(rowsNeedingNames.map(r => r.projectId)));
      for (const pid of projectIds) {
        try {
          const res = await apiService.listProjectTasks(pid);
          const data = res.data || res;
          const tasks: Task[] = Array.isArray(data) ? data : (data?.tasks || []);
          for (const t of tasks) {
            fetched.set(t.id, t.name);
          }
        } catch {
          // non-critical
        }
      }

      if (fetched.size > 0) {
        setRows(prev => prev.map(r => {
          if (r.taskId && !r.taskName && fetched.has(r.taskId)) {
            return { ...r, taskName: fetched.get(r.taskId)! };
          }
          return r;
        }));
      }
    };
    enrichTaskNames();
    // Only run when rows change and have missing names
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map(r => `${r.taskId}:${r.taskName}`).join(',')]);

  // -- Derived totals -------------------------------------------------------

  const dayTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const day of weekDays) {
      const iso = format(day, 'yyyy-MM-dd');
      totals[iso] = rows.reduce((sum, row) => sum + (row.dayMinutes[iso] || 0), 0);
    }
    return totals;
  }, [rows, weekDays]);

  const weekTotal = useMemo(
    () => rows.reduce((sum, row) => sum + row.totalMinutes, 0),
    [rows],
  );

  // Clear manually-added empty rows when the week changes
  useEffect(() => {
    setRows([]);
  }, [weekStartISO]);

  // -- Navigation -----------------------------------------------------------

  const goToPreviousWeek = () => setCurrentDate(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentDate(prev => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentDate(new Date());

  // -- Cell editing (inline for quick time) ---------------------------------

  const handleCellClick = (row: TimesheetRow, dateISO: string) => {
    if (!isEditable) return;
    const rk = makeRowKey(row.projectId, row.taskId);
    const currentMinutes = row.dayMinutes[dateISO] || 0;
    setEditCell({ rowKey: rk, date: dateISO });
    setEditValue(currentMinutes > 0 ? formatHoursMinutes(currentMinutes) : '');
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const handleCellSave = async () => {
    if (!editCell) return;
    const parsed = parseHoursMinutes(editValue);
    if (parsed === null) {
      showError('Invalid time format. Use H:MM or decimal hours (e.g., 2:30 or 2.5)');
      return;
    }

    const row = rows.find(r => makeRowKey(r.projectId, r.taskId) === editCell.rowKey);
    const existingEntries = row?.entries[editCell.date] || [];
    const currentMinutes = row?.dayMinutes[editCell.date] || 0;

    if (parsed === currentMinutes) {
      setEditCell(null);
      return;
    }

    setIsSaving(true);
    try {
      if (existingEntries.length === 1 && parsed > 0) {
        await updateEntry(existingEntries[0].id, { durationMinutes: parsed });
      } else if (existingEntries.length === 0 && parsed > 0) {
        const projectId = row?.projectId === '__no_project__' ? undefined : row?.projectId;
        await createEntry({
          projectId,
          taskId: row?.taskId || undefined,
          date: editCell.date,
          description: '',
          durationMinutes: parsed,
          isBillable: getProject(row?.projectId || '')?.isBillable,
        });
      } else if (existingEntries.length === 1 && parsed === 0) {
        await updateEntry(existingEntries[0].id, { durationMinutes: 0 });
      } else if (existingEntries.length > 1) {
        const delta = parsed - currentMinutes;
        const newFirst = Math.max(0, (Number(existingEntries[0].durationMinutes) || 0) + delta);
        await updateEntry(existingEntries[0].id, { durationMinutes: newFirst });
      }

      await fetchWeekEntries(weekStartISO);
    } catch (err: any) {
      showError(err.message || 'Failed to save time entry');
    } finally {
      setIsSaving(false);
      setEditCell(null);
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellSave();
    } else if (e.key === 'Escape') {
      setEditCell(null);
    }
  };

  // -- Note modal (time + description) ------------------------------------

  const handleNoteOpen = (e: React.MouseEvent, row: TimesheetRow, dateISO: string) => {
    e.stopPropagation(); // Don't trigger inline edit
    const existingEntries = row.entries[dateISO] || [];
    const entry = existingEntries[0];
    const currentMinutes = row.dayMinutes[dateISO] || 0;
    setCellModalRow(row);
    setCellModalDate(dateISO);
    setCellModalTime(currentMinutes > 0 ? formatHoursMinutes(currentMinutes) : '');
    setCellModalNote(entry?.description || '');
    setCellModalOpen(true);
  };

  const handleCellModalSave = async () => {
    if (!cellModalRow) return;
    const parsed = parseHoursMinutes(cellModalTime);
    if (parsed === null) {
      showError('Invalid time format. Use H:MM or decimal hours (e.g., 2:30 or 2.5)');
      return;
    }

    const existingEntries = cellModalRow.entries[cellModalDate] || [];
    const currentMinutes = cellModalRow.dayMinutes[cellModalDate] || 0;
    const entry = existingEntries[0];
    const noteChanged = (entry?.description || '') !== cellModalNote;
    const timeChanged = parsed !== currentMinutes;

    if (!timeChanged && !noteChanged) {
      setCellModalOpen(false);
      return;
    }

    if (parsed === 0 && !entry) {
      // Nothing to save
      setCellModalOpen(false);
      return;
    }

    setCellModalSaving(true);
    try {
      if (existingEntries.length === 1) {
        await updateEntry(entry.id, { durationMinutes: parsed, description: cellModalNote });
      } else if (existingEntries.length === 0 && parsed > 0) {
        const projectId = cellModalRow.projectId === '__no_project__' ? undefined : cellModalRow.projectId;
        await createEntry({
          projectId,
          taskId: cellModalRow.taskId || undefined,
          date: cellModalDate,
          description: cellModalNote,
          durationMinutes: parsed,
          isBillable: getProject(cellModalRow.projectId || '')?.isBillable,
        });
      } else if (existingEntries.length > 1) {
        const delta = parsed - currentMinutes;
        const newFirst = Math.max(0, (Number(existingEntries[0].durationMinutes) || 0) + delta);
        await updateEntry(existingEntries[0].id, { durationMinutes: newFirst, description: cellModalNote });
      }

      await fetchWeekEntries(weekStartISO);
      setCellModalOpen(false);
    } catch (err: any) {
      showError(err.message || 'Failed to save time entry');
    } finally {
      setCellModalSaving(false);
    }
  };

  // -- Remove row -----------------------------------------------------------

  const handleRemoveRow = async (row: TimesheetRow) => {
    // Collect all entry IDs across all days for this row
    const entryIds: string[] = [];
    for (const dayEntries of Object.values(row.entries)) {
      for (const entry of dayEntries) {
        entryIds.push(entry.id);
      }
    }

    if (entryIds.length === 0) {
      // Just remove the empty row from state
      setRows(prev => prev.filter(r => makeRowKey(r.projectId, r.taskId) !== makeRowKey(row.projectId, row.taskId)));
      return;
    }

    setIsSaving(true);
    try {
      for (const id of entryIds) {
        await deleteEntry(id);
      }
      await fetchWeekEntries(weekStartISO);
      showSuccess('Row removed');
    } catch (err: any) {
      showError(err.message || 'Failed to remove row');
    } finally {
      setIsSaving(false);
    }
  };

  // -- Add project+task row (modal) -----------------------------------------

  const availableProjects = useMemo(() => {
    return projects.filter(p => p.status === 'active');
  }, [projects]);

  const handleProjectSelect = async (project: Project | null) => {
    setSelectedProject(project);
    setSelectedTask(null);
    setTaskOptions([]);
    if (!project) return;

    setTasksLoading(true);
    try {
      const res = await apiService.listProjectTasks(project.id);
      const data = res.data || res;
      const tasks: Task[] = Array.isArray(data) ? data : (data?.tasks || []);
      setTaskOptions(tasks.filter(t => t.isActive));
    } catch {
      showError('Failed to load tasks for this project');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleAddRowConfirm = () => {
    if (!selectedProject || !selectedTask) return;

    const rowKey = makeRowKey(selectedProject.id, selectedTask.id);
    const exists = rows.some(r => makeRowKey(r.projectId, r.taskId) === rowKey);
    if (exists) {
      showError('This project + task combination is already on the timesheet');
      return;
    }

    const newRow: TimesheetRow = {
      projectId: selectedProject.id,
      taskId: selectedTask.id,
      projectName: selectedProject.name,
      taskName: selectedTask.name,
      projectColor: selectedProject.color,
      entries: {},
      dayMinutes: Object.fromEntries(weekDays.map(d => [format(d, 'yyyy-MM-dd'), 0])),
      totalMinutes: 0,
    };
    setRows(prev => [...prev, newRow]);
    setAddRowOpen(false);
    setSelectedProject(null);
    setSelectedTask(null);
    setTaskOptions([]);
  };

  const handleAddRowClose = () => {
    setAddRowOpen(false);
    setSelectedProject(null);
    setSelectedTask(null);
    setTaskOptions([]);
  };

  // -- Reset week -----------------------------------------------------------

  const handleResetWeek = async () => {
    setIsResetting(true);
    try {
      const entryIds: string[] = [];
      for (const row of rows) {
        for (const dayEntries of Object.values(row.entries)) {
          for (const entry of dayEntries) {
            entryIds.push(entry.id);
          }
        }
      }
      for (const id of entryIds) {
        await deleteEntry(id);
      }
      await fetchWeekEntries(weekStartISO);
      setResetConfirmOpen(false);
      showSuccess('Week has been reset');
    } catch (err: any) {
      showError(err.message || 'Failed to reset week');
    } finally {
      setIsResetting(false);
    }
  };

  // -- Submit / Unsubmit ---------------------------------------------------

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const response = await apiService.submitTimesheet(weekStartISO);
      const tsData = response.data || response;
      setTimesheet(tsData?.timesheet || tsData);
      showSuccess('Timesheet submitted for approval');
    } catch (err: any) {
      showError(err.message || 'Failed to submit timesheet');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnsubmit = async () => {
    setIsSaving(true);
    try {
      const response = await apiService.unsubmitTimesheet(weekStartISO);
      const tsData = response.data || response;
      setTimesheet(tsData?.timesheet || tsData);
      showSuccess('Timesheet recalled to draft');
    } catch (err: any) {
      showError(err.message || 'Failed to unsubmit timesheet');
    } finally {
      setIsSaving(false);
    }
  };

  // -- Day column color logic -----------------------------------------------

  const getDayColor = (minutes: number): string | undefined => {
    if (minutes > 600) return '#ef4444'; // > 10h red
    if (minutes > 480) return '#f59e0b'; // > 8h orange
    return undefined;
  };

  // -- Manager tab data loading ---------------------------------------------

  const loadPendingTimesheets = useCallback(async () => {
    setTabLoading(true);
    try {
      const response = await apiService.getPendingTimesheets();
      const data = response.data || response;
      setPendingTimesheets(Array.isArray(data) ? data : (data?.timesheets || []));
    } catch (err: any) {
      showError(err.message || 'Failed to load pending timesheets');
    } finally {
      setTabLoading(false);
    }
  }, [showError]);

  const loadTeamTimesheets = useCallback(async () => {
    setTabLoading(true);
    try {
      const response = await apiService.getTeamTimesheets(weekStartISO);
      const data = response.data || response;
      setTeamTimesheets(Array.isArray(data) ? data : (data?.timesheets || []));
    } catch (err: any) {
      showError(err.message || 'Failed to load team timesheets');
    } finally {
      setTabLoading(false);
    }
  }, [weekStartISO, showError]);

  useEffect(() => {
    if (activeTab === 'pending') loadPendingTimesheets();
    if (activeTab === 'team') loadTeamTimesheets();
  }, [activeTab, loadPendingTimesheets, loadTeamTimesheets]);

  const handleApprove = async (timesheetId: string) => {
    try {
      await apiService.approveTimesheet(timesheetId);
      showSuccess('Timesheet approved');
      loadPendingTimesheets();
    } catch (err: any) {
      showError(err.message || 'Failed to approve timesheet');
    }
  };

  const handleReject = async (timesheetId: string) => {
    try {
      await apiService.rejectTimesheet(timesheetId, 'Please review and resubmit');
      showSuccess('Timesheet rejected');
      loadPendingTimesheets();
    } catch (err: any) {
      showError(err.message || 'Failed to reject timesheet');
    }
  };

  // -- Manager: open team member timesheet detail --------------------------

  const openMemberDetail = async (userId: string, week?: string) => {
    const targetWeek = week || weekStartISO;
    setMemberDetailOpen(true);
    setMemberDetailLoading(true);
    setMemberEntries([]);
    setMemberTimesheet(null);
    setMemberDetailUser(null);
    try {
      const res = await apiService.getUserTimesheet(userId, targetWeek);
      const data = res.data || res;
      setMemberTimesheet(data?.timesheet || null);
      setMemberEntries(data?.entries || []);
      setMemberDetailUser(data?.user || { id: userId });
    } catch (err: any) {
      showError(err.message || 'Failed to load member timesheet');
      setMemberDetailOpen(false);
    } finally {
      setMemberDetailLoading(false);
    }
  };

  const handleMemberEntrySave = async (entryId: string, durationMinutes: number) => {
    if (!memberDetailUser) return;
    setMemberSaving(true);
    try {
      await apiService.adjustUserTimesheet(memberDetailUser.id, [
        { id: entryId, durationMinutes, action: 'update' },
      ]);
      showSuccess('Entry adjusted');
      // Reload
      const res = await apiService.getUserTimesheet(memberDetailUser.id, weekStartISO);
      const data = res.data || res;
      setMemberEntries(data?.entries || []);
      setMemberEditCell(null);
      loadTeamTimesheets();
    } catch (err: any) {
      showError(err.message || 'Failed to adjust entry');
    } finally {
      setMemberSaving(false);
    }
  };

  const handleMemberEntryDelete = async (entryId: string) => {
    if (!memberDetailUser) return;
    setMemberSaving(true);
    try {
      await apiService.adjustUserTimesheet(memberDetailUser.id, [
        { id: entryId, action: 'delete' },
      ]);
      showSuccess('Entry deleted');
      const res = await apiService.getUserTimesheet(memberDetailUser.id, weekStartISO);
      const data = res.data || res;
      setMemberEntries(data?.entries || []);
      loadTeamTimesheets();
    } catch (err: any) {
      showError(err.message || 'Failed to delete entry');
    } finally {
      setMemberSaving(false);
    }
  };

  const [memberResetConfirmOpen, setMemberResetConfirmOpen] = useState(false);

  const handleMemberReset = async () => {
    if (!memberDetailUser) return;
    setMemberSaving(true);
    try {
      await apiService.resetMemberTimesheet(memberDetailUser.id, weekStartISO);
      showSuccess('Timesheet reset successfully');
      setMemberResetConfirmOpen(false);
      setMemberDetailOpen(false);
      loadTeamTimesheets();
    } catch (err: any) {
      showError(err.message || 'Failed to reset timesheet');
    } finally {
      setMemberSaving(false);
    }
  };

  // -- Render ---------------------------------------------------------------

  const weekLabel = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Timesheet</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Tooltip title="Previous week">
              <IconButton size="small" onClick={goToPreviousWeek}>
                <ChevronLeft />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" fontWeight={500} sx={{ minWidth: 220, textAlign: 'center' }}>
              {weekLabel}
            </Typography>
            <Tooltip title="Next week">
              <IconButton size="small" onClick={goToNextWeek}>
                <ChevronRight />
              </IconButton>
            </Tooltip>
            <Tooltip title="Go to current week">
              <IconButton size="small" onClick={goToCurrentWeek} color="primary">
                <Today />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {activeTab === 'personal' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              icon={status === 'approved' ? <CheckCircle /> : status === 'rejected' ? <Cancel /> : undefined}
              label={STATUS_CONFIG[status].label}
              color={STATUS_CONFIG[status].color}
              variant="outlined"
              size="medium"
            />
            {isEditable && weekTotal > 0 && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RestartAlt />}
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={isSaving}
                  size="medium"
                >
                  Reset Week
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSubmit}
                  disabled={isSaving}
                  size="medium"
                >
                  Submit Week
                </Button>
              </>
            )}
            {status === 'submitted' && (
              <Button
                variant="outlined"
                startIcon={<Undo />}
                onClick={handleUnsubmit}
                disabled={isSaving}
                size="medium"
              >
                Unsubmit
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Tabs for personal/pending/team */}
      {isManager && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ px: 2, '& .MuiTab-root': { minHeight: 48 } }}
          >
            <Tab label="My Timesheet" value="personal" />
            <Tab label="Pending Approvals" value="pending" icon={<HourglassTop sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Team View" value="team" icon={<Groups sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Paper>
      )}

      {/* Rejection reason */}
      {activeTab === 'personal' && status === 'rejected' && timesheet?.rejectionReason && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 3 }}>
          <strong>Rejected:</strong> {timesheet.rejectionReason}
        </Alert>
      )}

      {/* Grid */}
      {activeTab === 'personal' && (isLoading ? (
        <Paper sx={{ p: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2, overflow: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 900 }}>
            {/* Table Header */}
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.9rem', py: 2 } }}>
                <TableCell sx={{ minWidth: 200, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                  Project / Task
                </TableCell>
                {weekDays.map((day, i) => (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{
                      minWidth: 72,
                      bgcolor: isToday(day) ? 'rgba(123, 109, 246, 0.08)' : undefined,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" display="block" fontWeight={600}>
                        {DAY_LABELS[i]}
                      </Typography>
                      <Typography variant="body2" display="block" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {format(day, 'd')}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
                <TableCell align="center" sx={{ minWidth: 72, fontWeight: 700 }}>
                  Total
                </TableCell>
                {isEditable && (
                  <TableCell sx={{ width: 48 }} />
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Data Rows */}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isEditable ? 10 : 9} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No time entries this week. Add a project row to get started.
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => {
                const rk = makeRowKey(row.projectId, row.taskId);
                return (
                  <TableRow
                    key={rk}
                    sx={{
                      '&:hover': { bgcolor: 'rgba(123, 109, 246, 0.04)' },
                      '& td': { py: 1 },
                    }}
                  >
                    {/* Project + Task name cell */}
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: row.projectColor,
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          {row.taskName && (
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200, lineHeight: 1.3 }}>
                              {row.taskName}
                            </Typography>
                          )}
                          <Typography
                            variant={row.taskName ? 'caption' : 'body2'}
                            fontWeight={row.taskName ? 400 : 600}
                            color={row.taskName ? 'text.secondary' : 'text.primary'}
                            noWrap
                            sx={{ maxWidth: 200, display: 'block', lineHeight: 1.3 }}
                          >
                            {row.projectName}
                          </Typography>
                          {row.clientName && (
                            <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block', fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.2 }}>
                              {row.clientName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Day cells */}
                    {weekDays.map((day, i) => {
                      const iso = format(day, 'yyyy-MM-dd');
                      const minutes = row.dayMinutes[iso] || 0;
                      const isEditing = editCell?.rowKey === rk && editCell?.date === iso;
                      const hasNote = !!(row.entries[iso]?.[0]?.description);

                      return (
                        <TableCell
                          key={i}
                          align="center"
                          onClick={() => !isEditing && handleCellClick(row, iso)}
                          sx={{
                            cursor: isEditable ? 'pointer' : 'default',
                            bgcolor: isToday(day) ? 'rgba(123, 109, 246, 0.04)' : undefined,
                            '&:hover': isEditable && !isEditing ? {
                              bgcolor: 'rgba(123, 109, 246, 0.08)',
                            } : undefined,
                            p: isEditing ? 0.5 : undefined,
                            transition: 'background-color 0.15s',
                          }}
                        >
                          {isEditing ? (
                            <TextField
                              inputRef={editInputRef}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={handleCellSave}
                              onKeyDown={handleCellKeyDown}
                              size="small"
                              variant="outlined"
                              placeholder="0:00"
                              disabled={isSaving}
                              inputProps={{
                                style: { textAlign: 'center', fontSize: '0.95rem', padding: '8px 4px' },
                              }}
                              sx={{ width: 72 }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: minutes > 0 ? 'text.primary' : 'text.disabled',
                                  fontWeight: minutes > 0 ? 500 : 400,
                                  fontVariantNumeric: 'tabular-nums',
                                }}
                              >
                                {minutes > 0 ? formatHoursMinutes(minutes) : '-'}
                              </Typography>
                              {(isEditable || hasNote) && (
                                <Tooltip title={hasNote ? row.entries[iso][0].description : 'Add note'} placement="top">
                                  <Box
                                    component="span"
                                    onClick={(e) => handleNoteOpen(e, row, iso)}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      cursor: 'pointer',
                                      color: hasNote ? '#fbbf24' : 'text.disabled',
                                      opacity: hasNote ? 1 : 0.4,
                                      '&:hover': { opacity: 1, color: '#fbbf24' },
                                      transition: 'opacity 0.15s, color 0.15s',
                                    }}
                                  >
                                    {hasNote
                                      ? <NoteIcon sx={{ fontSize: 16 }} />
                                      : <NoteOutlinedIcon sx={{ fontSize: 16 }} />}
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}

                    {/* Row total */}
                    <TableCell align="center">
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatHoursMinutes(row.totalMinutes)}
                      </Typography>
                    </TableCell>

                    {/* Remove row button */}
                    {isEditable && (
                      <TableCell align="center" sx={{ px: 0.5 }}>
                        <Tooltip title="Remove row">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveRow(row)}
                            disabled={isSaving}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}

              {/* Footer totals row */}
              {rows.length > 0 && (
                <TableRow sx={{
                  '& td': {
                    borderTop: '2px solid',
                    borderTopColor: 'divider',
                    py: 1.5,
                    fontWeight: 700,
                  },
                }}>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <Typography variant="body1" fontWeight={700}>Total</Typography>
                  </TableCell>
                  {weekDays.map((day, i) => {
                    const iso = format(day, 'yyyy-MM-dd');
                    const dayTotal = dayTotals[iso] || 0;
                    const color = getDayColor(dayTotal);
                    return (
                      <TableCell key={i} align="center" sx={{ bgcolor: isToday(day) ? 'rgba(123, 109, 246, 0.04)' : undefined }}>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          sx={{ color: color || 'text.primary', fontVariantNumeric: 'tabular-nums' }}
                        >
                          {formatHoursMinutes(dayTotal)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      color="primary"
                      sx={{ fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatHoursMinutes(weekTotal)}
                    </Typography>
                  </TableCell>
                  {isEditable && <TableCell />}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ))}

      {/* Add Project Row */}
      {activeTab === 'personal' && isEditable && !isLoading && (
        <Box sx={{ mt: 1 }}>
          <Button
            startIcon={<Add />}
            onClick={() => setAddRowOpen(true)}
            size="small"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Add Project Row
          </Button>
        </Box>
      )}

      {/* Summary footer */}
      {activeTab === 'personal' && !isLoading && rows.length > 0 && (
        <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Hours</Typography>
            <Typography variant="h6" fontWeight={700}>{formatHoursMinutes(weekTotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Billable Hours</Typography>
            <Typography variant="h6" fontWeight={700}>
              {formatHoursMinutes(
                rows
                  .filter(r => {
                    const p = getProject(r.projectId);
                    return p?.isBillable;
                  })
                  .reduce((sum, r) => sum + r.totalMinutes, 0)
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Projects</Typography>
            <Typography variant="h6" fontWeight={700}>
              {new Set(rows.map(r => r.projectId)).size}
            </Typography>
          </Box>
          {timesheet?.submittedAt && (
            <Box>
              <Typography variant="caption" color="text.secondary">Submitted</Typography>
              <Typography variant="h6" fontWeight={700}>
                {format(parseISO(timesheet.submittedAt), 'MMM d, h:mm a')}
              </Typography>
            </Box>
          )}
          {timesheet?.approvedAt && (
            <Box>
              <Typography variant="caption" color="text.secondary">Approved</Typography>
              <Typography variant="h6" fontWeight={700}>
                {format(parseISO(timesheet.approvedAt), 'MMM d, h:mm a')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ==================== Add Project + Task Dialog ==================== */}
      <Dialog open={addRowOpen} onClose={handleAddRowClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add Project Row</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              size="small"
              options={availableProjects}
              getOptionLabel={(opt) => opt.name}
              value={selectedProject}
              onChange={(_, value) => handleProjectSelect(value)}
              renderOption={(props, opt) => (
                <li {...props} key={opt.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: opt.color, flexShrink: 0 }} />
                    <Typography variant="body2">{opt.name}</Typography>
                    {opt.code && (
                      <Typography variant="caption" color="text.secondary">({opt.code})</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Project" placeholder="Select a project..." autoFocus />
              )}
            />

            {selectedProject && (
              <Autocomplete
                size="small"
                options={taskOptions}
                getOptionLabel={(opt) => opt.name}
                value={selectedTask}
                onChange={(_, value) => setSelectedTask(value)}
                loading={tasksLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Task"
                    placeholder={tasksLoading ? 'Loading tasks...' : 'Select a task...'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {tasksLoading ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={tasksLoading ? 'Loading...' : 'No tasks found. Create tasks in the project settings.'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddRowClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddRowConfirm}
            disabled={!selectedProject || !selectedTask}
          >
            Add Row
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Reset Week Confirmation ==================== */}
      <Dialog open={resetConfirmOpen} onClose={() => !isResetting && setResetConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Reset Week?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete all time entries for the week of <strong>{weekLabel}</strong>.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={isResetting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleResetWeek}
            disabled={isResetting}
            startIcon={isResetting ? <CircularProgress size={16} /> : <RestartAlt />}
          >
            {isResetting ? 'Resetting...' : 'Reset Week'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== Time + Note Dialog ==================== */}
      <Dialog open={cellModalOpen} onClose={() => !cellModalSaving && setCellModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span">{isEditable ? 'Time Entry' : 'Entry Details'}</Typography>
          {cellModalRow && (
            <Typography variant="body2" color="text.secondary">
              {cellModalRow.projectName}
              {cellModalRow.taskName ? ` — ${cellModalRow.taskName}` : ''}
              {cellModalDate ? ` · ${format(parseISO(cellModalDate), 'EEE, MMM d')}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Duration"
              value={cellModalTime}
              onChange={(e) => setCellModalTime(e.target.value)}
              disabled={cellModalSaving || !isEditable}
              placeholder="0:00"
              size="small"
              helperText={isEditable ? 'H:MM or decimal (e.g. 1:30 or 1.5)' : undefined}
              autoFocus={isEditable}
              inputProps={{ style: { fontSize: '1.1rem' } }}
              InputProps={{ readOnly: !isEditable }}
            />
            <TextField
              label={isEditable ? 'What did you work on?' : 'Note'}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              placeholder={isEditable ? 'Describe the work performed...' : ''}
              value={cellModalNote}
              onChange={(e) => setCellModalNote(e.target.value)}
              disabled={cellModalSaving || !isEditable}
              variant="outlined"
              InputProps={{ readOnly: !isEditable }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {isEditable ? (
            <>
              <Button onClick={() => setCellModalOpen(false)} disabled={cellModalSaving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleCellModalSave}
                disabled={cellModalSaving}
              >
                {cellModalSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Button onClick={() => setCellModalOpen(false)}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Pending Approvals View */}
      {activeTab === 'pending' && (
        <Box>
          {tabLoading ? (
            <Paper sx={{ p: 2 }}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Paper>
          ) : pendingTimesheets.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <HourglassTop sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No pending timesheets</Typography>
              <Typography variant="body2" color="text.disabled">
                All timesheets have been reviewed.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>Team Member</TableCell>
                    <TableCell>Week</TableCell>
                    <TableCell align="center">Total Hours</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingTimesheets.map((ts: any) => (
                    <TableRow
                      key={ts.id || ts.userId + ts.weekStart}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openMemberDetail(ts.userId, ts.weekStart)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {ts.userName || ts.userEmail || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ts.weekStart || ts.week || '--'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600}>
                          {ts.totalHours != null ? `${ts.totalHours.toFixed(1)}h` : formatHoursMinutes(ts.totalMinutes || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label="Pending" color="warning" size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          startIcon={<CheckCircle />}
                          onClick={() => handleApprove(ts.id)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<Cancel />}
                          onClick={() => handleReject(ts.id)}
                        >
                          Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Team View */}
      {activeTab === 'team' && (
        <Box>
          {tabLoading ? (
            <Paper sx={{ p: 2 }}>
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Paper>
          ) : teamTimesheets.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Groups sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No team timesheets</Typography>
              <Typography variant="body2" color="text.disabled">
                No team member data available for this week.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>Team Member</TableCell>
                    {DAY_LABELS.map(day => (
                      <TableCell key={day} align="center">{day}</TableCell>
                    ))}
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamTimesheets.map((member: any) => (
                    <TableRow key={member.userId || member.id} hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openMemberDetail(member.userId)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {member.userName || member.userEmail || 'Unknown'}
                        </Typography>
                      </TableCell>
                      {DAY_LABELS.map((_, i) => (
                        <TableCell key={i} align="center">
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {member.dailyHours?.[i] != null
                              ? `${member.dailyHours[i].toFixed(1)}`
                              : '-'}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {member.totalHours != null
                            ? `${member.totalHours.toFixed(1)}h`
                            : formatHoursMinutes(member.totalMinutes || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={STATUS_CONFIG[member.status as TimesheetStatus]?.label || member.status || 'draft'}
                          color={STATUS_CONFIG[member.status as TimesheetStatus]?.color || 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="View & Adjust">
                          <IconButton size="small" onClick={() => openMemberDetail(member.userId)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ==================== Member Detail / Adjust Dialog ==================== */}
      <Dialog
        open={memberDetailOpen}
        onClose={() => !memberSaving && setMemberDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">
              {memberDetailUser
                ? `${memberDetailUser.firstName || ''} ${memberDetailUser.lastName || ''}`.trim() || memberDetailUser.email || 'Team Member'
                : 'Team Member'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }} component="span">
              {weekLabel}
            </Typography>
          </Box>
          {memberTimesheet && (
            <Chip
              label={STATUS_CONFIG[memberTimesheet.status as TimesheetStatus]?.label || memberTimesheet.status || 'draft'}
              color={STATUS_CONFIG[memberTimesheet.status as TimesheetStatus]?.color || 'default'}
              size="small"
              variant="outlined"
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {memberDetailLoading ? (
            <Box sx={{ py: 4 }}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : memberEntries.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No time entries for this week.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Duration</TableCell>
                    <TableCell align="center">Billable</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberEntries.map((entry: TimeEntry) => {
                    const isEditingDuration = memberEditCell?.entryId === entry.id && memberEditCell?.field === 'duration';
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.date ? format(parseISO(entry.date), 'EEE, MMM d') : '--'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {entry.projectId ? (getProject(entry.projectId)?.name || entry.projectId) : 'No Project'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {entry.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {isEditingDuration ? (
                            <TextField
                              size="small"
                              value={memberEditValue}
                              onChange={(e) => setMemberEditValue(e.target.value)}
                              onBlur={() => {
                                const parsed = parseHoursMinutes(memberEditValue);
                                if (parsed !== null && parsed !== Number(entry.durationMinutes)) {
                                  handleMemberEntrySave(entry.id, parsed);
                                } else {
                                  setMemberEditCell(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const parsed = parseHoursMinutes(memberEditValue);
                                  if (parsed !== null) {
                                    handleMemberEntrySave(entry.id, parsed);
                                  }
                                } else if (e.key === 'Escape') {
                                  setMemberEditCell(null);
                                }
                              }}
                              autoFocus
                              disabled={memberSaving}
                              placeholder="0:00"
                              inputProps={{ style: { textAlign: 'center', padding: '6px 4px', width: 60 } }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                cursor: 'pointer',
                                fontVariantNumeric: 'tabular-nums',
                                '&:hover': { color: 'primary.main' },
                              }}
                              onClick={() => {
                                setMemberEditCell({ entryId: entry.id, field: 'duration' });
                                setMemberEditValue(formatHoursMinutes(Number(entry.durationMinutes) || 0));
                              }}
                            >
                              {formatHoursMinutes(Number(entry.durationMinutes) || 0)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={entry.isBillable ? 'Yes' : 'No'}
                            size="small"
                            color={entry.isBillable ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ minWidth: 40 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Delete entry">
                            <IconButton
                              size="small"
                              onClick={() => handleMemberEntryDelete(entry.id)}
                              disabled={memberSaving}
                              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total row */}
                  <TableRow sx={{ '& td': { borderTop: '2px solid', borderTopColor: 'divider', fontWeight: 700 } }}>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" fontWeight={700}>Total</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700} color="primary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatHoursMinutes(memberEntries.reduce((sum, e) => sum + (Number(e.durationMinutes) || 0), 0))}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<RestartAlt />}
            onClick={() => setMemberResetConfirmOpen(true)}
            disabled={memberSaving}
          >
            Reset Week
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {memberSaving && <CircularProgress size={20} />}
            <Button onClick={() => setMemberDetailOpen(false)} disabled={memberSaving}>
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ==================== Member Reset Confirmation ==================== */}
      <Dialog open={memberResetConfirmOpen} onClose={() => !memberSaving && setMemberResetConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Reset Team Member's Timesheet?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will delete all time entries for{' '}
            <strong>
              {memberDetailUser
                ? `${memberDetailUser.firstName || ''} ${memberDetailUser.lastName || ''}`.trim() || memberDetailUser.email
                : 'this team member'}
            </strong>{' '}
            for the week of <strong>{weekLabel}</strong> and reset the timesheet to draft.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberResetConfirmOpen(false)} disabled={memberSaving}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleMemberReset}
            disabled={memberSaving}
            startIcon={memberSaving ? <CircularProgress size={16} /> : <RestartAlt />}
          >
            {memberSaving ? 'Resetting...' : 'Reset Week'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimesheetPage;
